import React from "react";
import styled from "styled-components";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import PrimaryButton from "components/button/PrimaryButton";
import SecondaryButton from "components/button/SecondaryButton";
import { isMobile } from "@kubera/common";

const isMobileDevice = isMobile();

const ConditionalContainerStyled = isMobileDevice
  ? styled(DialogOverlay)`
      width: 100vw;
      height: 100vh;

      & > div {
        padding: 0 2.25rem;
      }

      * {
        box-sizing: border-box;
      }
    `
  : styled(DialogOverlay)``;

const GenericDialog = isMobileDevice
  ? styled(Dialog)`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: 10.1875rem;
    `
  : styled(Dialog)`
      max-width: 43.75rem;
      width: 100%;
      min-width: ${props => (props.width ? props.width : props.hasTertiaryButton === true ? "37.5rem" : "35rem")};
      height: fit-content;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: 10.1875rem;
      box-sizing: border-box;
    `;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${isMobileDevice ? null : "2.5rem"};
  padding: ${isMobileDevice ? "2.5rem 1.875rem" : null};
`;

const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 1.375rem;
  line-height: 1.75rem;
  display: flex;
  align-items: flex-end;
  font-feature-settings: "ss01" on;
  text-align: left;
  white-space: pre-wrap;
`;

const Description = styled.div`
  flex: 1;
  margin-top: ${isMobileDevice ? "0.125rem" : "0.625rem"};
  font-style: normal;
  font-weight: normal;
  font-size: 0.8125rem;
  line-height: ${isMobileDevice ? "1.3125rem" : "150%"};
  font-feature-settings: "ss01" on;
  text-align: left;
  ${props => (props.descriptionStyle ? props.descriptionStyle : "")}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: ${isMobileDevice ? "column" : "row"};
`;

const ErrorText = styled.p`
  margin: 0;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  visibility: ${props => (!props.error === true ? "hidden" : "visible")};
  font-size: 0.6875rem;
  text-align: left;
  letter-spacing: 0.01em;
  color: ${props => props.theme.errorCLR};
`;

const PositiveButton = styled(PrimaryButton)`
  min-width: 9.6875rem;
  padding: 0 1.5rem;
  box-sizing: border-box;
  font-size: ${isMobileDevice ? "0.875rem" : "0.75rem"};
  width: ${props => (props.width ? props.width : "auto")};
`;

const NegativeButton = styled(SecondaryButton)`
  margin-left: ${isMobileDevice ? "0" : "1.25rem"};
  margin-top: ${isMobileDevice ? "0.625rem" : "0"};
  min-width: 9.6875rem;
  padding: 0 1.5rem;
  font-size: ${isMobileDevice ? "0.875rem" : "0.75rem"};
`;

const Note = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 0.975rem;
  color: #000000b2;
  margin-top: 0.5625rem;
`;

class ConfirmationDialog extends React.Component {
  constructor(props) {
    super(props);

    this.handleOverlayDismiss = this.handleOverlayDismiss.bind(this);
  }

  handleOverlayDismiss() {
    if (this.props.canUserDismiss === true) {
      if (this.props.onDismiss) {
        this.props.onDismiss();
      }
    }
  }

  render() {
    const title = this.props.title;
    const description = this.props.description;
    const positiveButtonTitle = this.props.positiveButtonTitle;
    const negativeButtonTitle = this.props.negativeButtonTitle;
    const tertiaryButtonTitle = this.props.tertiaryButtonTitle;
    const isLoading = this.props.isLoading;
    const errorMessage = this.props.errorMessage;

    return (
      <ConditionalContainerStyled onDismiss={this.handleOverlayDismiss}>
        <GenericDialog
          className={this.props.className}
          width={this.props.dialogWidth}
          hasTertiaryButton={!tertiaryButtonTitle === false}
        >
          <Container>
            <Title>{title}</Title>
            {!description === false && (
              <Description
                descriptionStyle={this.props.descriptionStyle}
                dangerouslySetInnerHTML={{
                  __html: description
                }}
              />
            )}
            <ErrorText error={errorMessage}>{!errorMessage === true ? "" : errorMessage}</ErrorText>
            <ButtonContainer>
              <PositiveButton
                onClick={this.props.handlePositiveButtonClick}
                title={positiveButtonTitle}
                isLoading={isLoading}
                isDestructive={this.props.isDestructive}
                data-cy="positiveButton"
                width={this.props.positiveButtonWidth}
              />
              {!negativeButtonTitle === false && (
                <NegativeButton
                  onClick={this.props.handleNegativeButtonClick}
                  title={negativeButtonTitle}
                  isDisabled={isLoading === true}
                  data-cy="negativeButton"
                />
              )}
              {!tertiaryButtonTitle === false && (
                <NegativeButton
                  onClick={this.props.handleTertiaryButtonClick}
                  title={tertiaryButtonTitle}
                  isDisabled={isLoading === true}
                  data-cy="tertiaryButton"
                />
              )}
            </ButtonContainer>
            {this.props.note && <Note>{this.props.note}</Note>}
          </Container>
        </GenericDialog>
      </ConditionalContainerStyled>
    );
  }
}

export default ConfirmationDialog;
